import 'styled-components/macro';
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme, Box, Typography, ErrorState, LoadingState, Flag, Button } from '@harver/shared-ui';

import { AlreadyUnavailableInterview } from '../shared/views/AlreadyUnavailableInterview';
import { Cancelled } from '../confirmation/Cancelled';
import { Expired } from '../shared/views/Expired';
import { CompletedInterview } from '../shared/views/CompletedInterview';
import { DatePicker } from './DatePicker';
import { EmptySlots } from './EmptySlots';
import { TimePicker } from './TimePicker';
import { TimezonePicker } from './TimezonePicker';
import { Loader as InitialLoader } from './Loader';
import {
    getAvailableSlots,
    regroupSlotsByTimezone,
    scheduleInterview,
    Slot,
    formatTimeSlot,
    formatDay,
    TimePickerLinkStatus,
    WeekdayTypes,
} from './service';
import { Wrapper, ConfirmationMessage, PickerWrapper, StyledLink } from './StyledElements';
import { getCurrentTimezone, Timezone } from '../../utils/timezone/service';
import { useContent } from '../../branding/content.context';
import { INTERVIEW_STATUS, SETTINGS_ERRORS } from '../shared/domains';
import { SettingDisabled } from '../shared/views/SettingDisabled';
import CookieBanner from '../../components/CookieBanner';

interface Props {
    history: {
        push: (path: string) => void;
    };
}
interface State {
    interviewStatus: TimePickerLinkStatus['interviewStatus'] | '';
    interviewDuration: TimePickerLinkStatus['interviewDuration'];
    slots: TimePickerLinkStatus['slots'];
}

export const Scheduler = (props: Props): React.ReactElement => {
    const theme = useTheme();
    const { scheduler: LANG, locale } = useContent();

    const params = new URLSearchParams(useLocation().search);
    const calendarEventId = params.get('ref') as string;

    const [isInitialLoading, setInitialLoading] = useState(true);
    const [confirming, confirm] = useState(false);
    const [error, setError] = useState(false);
    const [selectedTimezone, setSelectedTimezone] = useState<Timezone>(getCurrentTimezone());
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<Slot | null>(null);
    const [isSlotUnavailable, setIsSlotUnavailable] = useState(false);
    const [confirmationError, setConfirmationError] = useState(false);
    const [interviewDisabled, setInterviewDisabled] = useState(false);

    const [state, setState] = useState<State>({
        interviewStatus: '',
        slots: null,
        interviewDuration: 0,
    });

    const isUnmounted = useRef(false);
    useEffect(() => {
        getAvailableSlots(calendarEventId, selectedTimezone.tzid)
            .then((resp) => {
                if (resp.interviewStatus === INTERVIEW_STATUS.scheduled) {
                    isUnmounted.current = true;
                    props.history.push(`/interview/confirmation?ref=${calendarEventId}`);
                }

                // select the first date
                if (resp.slots) setSelectedDate(Object.keys(resp.slots)[0]);

                setState({
                    interviewStatus: resp.interviewStatus,
                    slots: resp.slots,
                    interviewDuration: resp.interviewDuration,
                });
            })
            .catch((err) => {
                const errorCode = err?.response?.data?.context?.code;
                if (errorCode === SETTINGS_ERRORS.settingDisabled) {
                    setInterviewDisabled(true);
                    return;
                }

                setError(true);
            })
            .finally(() => {
                if (!isUnmounted.current) {
                    setInitialLoading(false);
                }
            });
    }, [calendarEventId, props.history]);

    useEffect(() => {
        if (!state.slots) return;

        const regroupedAvailableSlots = regroupSlotsByTimezone(state.slots, selectedTimezone.tzid);

        if (regroupedAvailableSlots) {
            setSelectedDate(Object.keys(regroupedAvailableSlots)[0]);
            setSelectedTimeSlot(null);
        }

        setState((prevState) => ({ ...prevState, slots: regroupedAvailableSlots }));
    }, [selectedTimezone.tzid]);

    if (isInitialLoading) return <InitialLoader />;

    if (confirming) return <LoadingState />;

    if (error) {
        document.title = 'Something went wrong';
        return <ErrorState testId="timepicker.errorScreen" onReloadClick={() => window.location.reload()} />;
    }

    if (interviewDisabled) return <SettingDisabled />;

    if (state.interviewStatus === INTERVIEW_STATUS.unavailable) return <AlreadyUnavailableInterview />;

    if (state.interviewStatus === INTERVIEW_STATUS.cancelled) return <Cancelled />;

    if (state.interviewStatus === INTERVIEW_STATUS.expired) return <Expired />;

    if (state.interviewStatus === INTERVIEW_STATUS.completed) return <CompletedInterview />;

    if (!state.slots) return <EmptySlots calendarEventId={calendarEventId} {...props} />;

    const onDatePicked = (value) => {
        setSelectedTimeSlot(null);
        setSelectedDate(value);
    };

    const handleTimezoneChange = (timezone) => {
        setSelectedTimezone(timezone);
    };

    const onConfirm = async () => {
        confirm(true);

        const data = {
            startTime: (selectedTimeSlot as Slot).start,
            endTime: (selectedTimeSlot as Slot).end,
            participants: (selectedTimeSlot as Slot).participants.map((participant) => participant.cronofyAccountId),
            calendarEventId,
            candidatePreferredTimezone: selectedTimezone.tzid || Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        scheduleInterview(data, selectedTimezone.tzid)
            .then((resp) => {
                if (resp.hasScheduled) {
                    isUnmounted.current = true;
                    props.history.push(`/interview/confirmation?ref=${calendarEventId}`);
                    return;
                }

                if (resp.isSlotAvailable) {
                    setConfirmationError(true);
                } else {
                    setIsSlotUnavailable(true);
                }

                setSelectedTimeSlot(null);
                if (resp.slots) setSelectedDate(Object.keys(resp.slots)[0]);

                setState({
                    ...state,
                    slots: resp.slots,
                });
            })
            .catch((err) => {
                const errorCode = err?.response?.data?.context?.code;
                if (errorCode === SETTINGS_ERRORS.settingDisabled) {
                    setInterviewDisabled(true);
                    return;
                }

                setError(true);
            })
            .finally(() => {
                if (!isUnmounted.current) {
                    confirm(false);
                }
            });
    };

    return (
        <Wrapper css={{ paddingBottom: theme.sizes.xxLarge }}>
            <Typography id="interview-day" variant="h2" css={{ marginBottom: theme.sizes.small, textAlign: 'center' }}>
                {LANG.p1}
            </Typography>

            {isSlotUnavailable && (
                <Flag variant="info" message={LANG.availableSlotNotFound} css={{ marginBottom: theme.sizes.small }} />
            )}

            {confirmationError && (
                <Flag variant="info" message={LANG.confirmationFailed} css={{ marginBottom: theme.sizes.small }} />
            )}

            <Box variant="body1" css={{ marginBottom: theme.sizes.large, textAlign: 'center' }}>
                <TimezonePicker timezoneId={selectedTimezone.tzid} onChange={handleTimezoneChange} />
            </Box>

            <PickerWrapper role="group" aria-labelledby="interview-day">
                {Object.keys(state.slots).map((date) => (
                    <DatePicker
                        key={date}
                        date={date}
                        isSelected={selectedDate === date}
                        onSelect={onDatePicked}
                        locale={locale}
                    />
                ))}
            </PickerWrapper>

            <Typography variant="body1" css={{ marginTop: theme.sizes.large, textAlign: 'center' }}>
                {LANG.requestNewSlot}{' '}
                <StyledLink css={{ marginTop: theme.sizes.large }} to={`/interview/unavailable?ref=${calendarEventId}`}>
                    {LANG.requestNewSlotLinkLbl}
                </StyledLink>
            </Typography>

            {selectedDate && (
                <Box
                    css={{
                        marginTop: theme.sizes.xxLarge,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography id="interview-time" variant="h2" css={{ marginBottom: theme.sizes.small }}>
                        {LANG.p3}
                    </Typography>

                    <Typography variant="body1" css={{ marginBottom: theme.sizes.large }}>
                        {LANG.p4.replace('{interviewDuration}', state.interviewDuration.toString())}
                    </Typography>

                    <PickerWrapper role="group" aria-labelledby="interview-time" testId="timepicker-container">
                        <TimePicker
                            slots={state.slots[selectedDate]}
                            selectedSlot={selectedTimeSlot}
                            onSelect={(timeSlot) => setSelectedTimeSlot(timeSlot)}
                            timezoneId={selectedTimezone.tzid}
                            locale={locale}
                        />
                    </PickerWrapper>
                </Box>
            )}

            {selectedTimeSlot && (
                <>
                    <ConfirmationMessage css={{ textAlign: 'center' }}>
                        <Typography variant="h3" css={{ marginBottom: theme.sizes.small }}>
                            {formatDay(selectedTimeSlot.start, selectedTimezone.tzid, locale, WeekdayTypes.LONG)}
                        </Typography>

                        <Typography variant="subtitle1">
                            {formatTimeSlot(selectedTimeSlot, selectedTimezone.tzid, locale).startTime} -{' '}
                            {formatTimeSlot(selectedTimeSlot, selectedTimezone.tzid, locale).endTime}
                        </Typography>

                        <Typography variant="body1">{selectedTimezone.country}</Typography>
                    </ConfirmationMessage>

                    <Button onClick={onConfirm} color="primary" testId="confirmation-btn">
                        {LANG.btn}
                    </Button>
                </>
            )}
            <CookieBanner />
        </Wrapper>
    );
};
